import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from './ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const HomeHero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.5]);

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="relative min-h-[90vh] flex items-center overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/70 via-primary/60 to-dark/80 mix-blend-multiply z-10"></div>
        <motion.div style={{ y, opacity }} className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1602615576820-ea14cf3e476a"
            alt="Elegant floral arrangement"
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
      </div>

      {/* Content container */}
      <div className="container mx-auto px-4 relative z-30 py-20 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            variants={fadeIn}
            className="inline-block px-4 py-1.5 mb-8 bg-secondary/20 border border-secondary/30 rounded-full"
          >
            <span className="text-sm font-medium tracking-wider text-light uppercase">
              Flowers as Refined as Your Taste
            </span>
          </motion.div>

          <motion.h1
            variants={fadeIn}
            className="text-5xl md:text-6xl lg:text-7xl font-bold font-heading mb-6 leading-tight text-white"
          >
            Effortlessly Elegant
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary via-mint to-peach block mt-2">
              & Curated
            </span>
          </motion.h1>

          <motion.p
            variants={fadeIn}
            className="text-lg md:text-xl text-light/90 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Tailored, luxurious blooms brought to your door, on your schedule.
          </motion.p>

          <motion.div
            variants={fadeIn}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12"
          >
            <Button
              variant="pastel"
              size="lg"
              className="shadow-glow hover:shadow-lg transition-all duration-500 px-10 py-6 text-base"
              onClick={() => {
                const featuredSection =
                  document.getElementById('featured-flowers');
                if (featuredSection) {
                  featuredSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Explore Flowers
            </Button>

            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              }}
              className="hidden md:flex items-center text-light/80 text-sm"
            >
              <FontAwesomeIcon icon={faArrowDown} className="mr-2" />
              Scroll to discover
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Subtle decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark/50 to-transparent z-20"></div>

      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-dark/30 to-transparent z-20"></div>

      {/* Removed "As featured in" section as requested */}
    </motion.section>
  );
};

export default HomeHero;
